import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import MobileTable from '../common/MobileTable';
import CustomDropdown from '../common/CustomDropdown';
import DeleteConfirmModal from '../common/DeleteConfirmModal';

const DistributorsTab = () => {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDistributorHistory, setShowDistributorHistory] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [activeTab, setActiveTab] = useState('outstanding');
  const [distributorHistory, setDistributorHistory] = useState([]);
  const [distributorLedger, setDistributorLedger] = useState(null); // Will store {openingBalance, closingBalance, transactions}
  const [distributorOrders, setDistributorOrders] = useState([]);
  const [unpaidBills, setUnpaidBills] = useState([]);
  const [openingBalanceLimit, setOpeningBalanceLimit] = useState(null);
  const [editingDistributor, setEditingDistributor] = useState(null);
  const [ledgerDateRange, setLedgerDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    openingBalance: ''
  });
  const [paymentData, setPaymentData] = useState({
    distributorId: '',
    paymentType: 'order_payment',
    amount: '',
    paymentFrom: 'bank_balance',
    billNumber: '',
    orderId: '',
    notes: '',
    paymentDate: new Date().toISOString().split('T')[0]
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = async () => {
    try {
      const response = await api.get('/distributors');
      setDistributors(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch distributors');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributorHistory = async (distributorId, startDate = '', endDate = '') => {
    try {
      // Build query params for date range
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);
      const queryString = queryParams.toString();

      // Fetch both payment history and ledger from NEW payment system
      const [historyRes, ledgerRes] = await Promise.all([
        api.get(`/distributor-payments/${distributorId}/payments`), // NEW endpoint
        api.get(`/distributor-payments/${distributorId}/ledger${queryString ? '?' + queryString : ''}`)
      ]);
      setDistributorHistory(historyRes.data);
      setDistributorLedger(ledgerRes.data);
    } catch (err) {
      console.error('Failed to fetch distributor history:', err);
      setError('Failed to fetch payment history');
    }
  };

  const fetchUnpaidBills = async (distributorId) => {
    try {
      const response = await api.get(`/distributor-payments/${distributorId}/unpaid-bills`);
      setUnpaidBills(response.data);
    } catch (err) {
      console.error('Failed to fetch unpaid bills:', err);
      setError('Failed to fetch unpaid bills');
    }
  };

  const fetchOpeningBalanceLimit = async (distributorId) => {
    try {
      const response = await api.get(`/distributor-payments/${distributorId}/opening-balance-limit`);
      setOpeningBalanceLimit(response.data);
    } catch (err) {
      console.error('Failed to fetch opening balance limit:', err);
      setError('Failed to fetch opening balance limit');
    }
  };

  const fetchDistributorOrders = async (distributorId) => {
    try {
      const response = await api.get('/orders');
      // Filter orders for this distributor
      const filteredOrders = response.data.filter(order => order.distributor_id === distributorId);
      setDistributorOrders(filteredOrders);
    } catch (err) {
      console.error('Failed to fetch distributor orders:', err);
      setError('Failed to fetch orders');
    }
  };

  const handleShowDistributorHistory = async (distributor) => {
    setSelectedDistributor(distributor);
    setActiveTab('outstanding');
    await Promise.all([
      fetchDistributorHistory(distributor.id),
      fetchUnpaidBills(distributor.id),
      fetchDistributorOrders(distributor.id)
    ]);
    setShowDistributorHistory(true);
  };

  const handleCloseDistributorHistory = () => {
    setShowDistributorHistory(false);
    setSelectedDistributor(null);
    setActiveTab('outstanding');
    setDistributorHistory([]);
    setDistributorLedger(null);
    setDistributorOrders([]);
    setUnpaidBills([]);
    setLedgerDateRange({ startDate: '', endDate: '' });
  };

  const handleOpenModal = (distributor = null) => {
    if (distributor) {
      setEditingDistributor(distributor);
      setFormData({
        name: distributor.name,
        openingBalance: distributor.opening_balance || '',
        amountOutstanding: distributor.amount_outstanding || ''
      });
    } else {
      setEditingDistributor(null);
      setFormData({
        name: '',
        openingBalance: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDistributor(null);
    setFormData({
      name: '',
      openingBalance: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingDistributor) {
        await api.put(`/distributors/${editingDistributor.id}`, formData);
      } else {
        // When creating, set both opening_balance and amountOutstanding to the same value
        const payload = {
          ...formData,
          amountOutstanding: formData.openingBalance || 0
        };
        await api.post('/distributors', payload);
      }
      await fetchDistributors();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleOpenPaymentModal = () => {
    setPaymentData({
      distributorId: '',
      paymentType: 'order_payment',
      amount: '',
      paymentFrom: 'bank_balance',
      billNumber: '',
      orderId: '',
      notes: '',
      paymentDate: new Date().toISOString().split('T')[0]
    });
    setUnpaidBills([]);
    setOpeningBalanceLimit(null);
    setShowPaymentModal(true);
    setError('');
    setSuccess('');
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentData({
      distributorId: '',
      paymentType: 'order_payment',
      amount: '',
      paymentFrom: 'bank_balance',
      billNumber: '',
      orderId: '',
      notes: '',
      paymentDate: new Date().toISOString().split('T')[0]
    });
    setUnpaidBills([]);
    setOpeningBalanceLimit(null);
  };

  const handlePayDistributor = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!paymentData.distributorId || !paymentData.amount) {
      setError('Please select a distributor and enter amount');
      return;
    }

    const amount = parseFloat(paymentData.amount);
    if (amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    // Validate bill number is selected for order_payment
    if (paymentData.paymentType === 'order_payment' && !paymentData.orderId) {
      setError('Please select a bill number for order payment');
      return;
    }

    // Validate opening_balance_payment doesn't exceed limit
    if (paymentData.paymentType === 'opening_balance_payment' && openingBalanceLimit) {
      if (amount > openingBalanceLimit.remaining_limit) {
        setError(`Payment exceeds opening balance limit. Maximum allowed: ₹${openingBalanceLimit.remaining_limit.toFixed(2)}`);
        return;
      }
    }

    const selectedDistributor = distributors.find(d => d.id === paymentData.distributorId);
    if (!selectedDistributor) {
      setError('Distributor not found');
      return;
    }

    try {
      const requestData = {
        distributorId: paymentData.distributorId,
        paymentType: paymentData.paymentType,
        amount: amount,
        paymentFrom: paymentData.paymentFrom,
        paymentDate: paymentData.paymentDate,
        notes: paymentData.notes || null
      };

      // Add bill-specific fields for order_payment
      if (paymentData.paymentType === 'order_payment') {
        requestData.orderId = paymentData.orderId;
        requestData.billNumber = paymentData.billNumber;
      }

      await api.post('/distributor-payments/pay', requestData);

      const paymentTypeLabel = paymentData.paymentType === 'advance' ? 'advance payment' : 'payment';
      setSuccess(`Successfully recorded ${paymentTypeLabel} of ₹${amount.toFixed(2)} for ${selectedDistributor.name}`);
      await fetchDistributors();
      handleClosePaymentModal();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    }
  };

  const handleRefreshLedger = async () => {
    if (selectedDistributor) {
      await fetchDistributorHistory(
        selectedDistributor.id,
        ledgerDateRange.startDate,
        ledgerDateRange.endDate
      );
    }
  };

  const handleDeletePayment = (payment) => {
    setDeleteTarget(payment);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePayment = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    setError('');

    try {
      await api.delete(`/distributor-payments/payment/${deleteTarget.id}`);
      setSuccess(`Payment of ₹${parseFloat(deleteTarget.amount).toFixed(2)} deleted successfully`);

      // Refresh data
      await Promise.all([
        fetchDistributors(),
        selectedDistributor ? fetchDistributorHistory(selectedDistributor.id) : Promise.resolve()
      ]);

      setShowDeleteConfirm(false);
      setDeleteTarget(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete payment');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const columns = [
    { key: 'name', label: 'Name' },
    {
      key: 'amount_outstanding',
      label: 'Amount Outstanding',
      render: (dist) => (
        <span style={{ fontWeight: '700', color: '#e91e63', fontSize: '1.125rem' }}>
          ₹{parseFloat(dist.amount_outstanding || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (dist) => new Date(dist.created_at).toLocaleDateString()
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (dist) => (
        <div className="action-buttons">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShowDistributorHistory(dist);
            }}
            className="btn btn-primary"
          >
            History
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal(dist);
            }}
            className="btn btn-secondary"
          >
            Edit
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      {success && <div className="success" style={{ marginBottom: '1rem' }}>{success}</div>}
      {error && !showModal && !showPaymentModal && (
        <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      <div className="mobile-stack" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>
        <h2 style={{ color: '#000', margin: 0, fontSize: 'clamp(1.5rem, 5vw, 2rem)', fontWeight: '700', letterSpacing: '0.5px' }}>
          Distributors
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleOpenPaymentModal}
            className="btn btn-primary"
            style={{ background: '#2196F3', borderColor: '#000' }}
          >
            Pay Distributor
          </button>
          <button onClick={() => handleOpenModal()} className="btn btn-success">
            Add New
          </button>
        </div>
      </div>

      <MobileTable
        columns={columns}
        data={distributors}
        onRowClick={handleShowDistributorHistory}
        enableSearch={true}
        enableSort={true}
        defaultSortKey="name"
        defaultSortOrder="asc"
      />

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingDistributor ? 'Edit Distributor' : 'Add New Distributor'}</h2>
            {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              {!editingDistributor && (
                <div className="form-group">
                  <label htmlFor="openingBalance">Opening Balance (Optional)</label>
                  <input
                    type="number"
                    id="openingBalance"
                    className="form-control"
                    value={formData.openingBalance}
                    onChange={(e) => setFormData({ ...formData, openingBalance: e.target.value })}
                    step="0.01"
                    min="0"
                    placeholder="₹ 0.00"
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                    Initial outstanding amount when first adding this distributor. This sets both the opening balance (for ledger) and initial amount outstanding.
                  </small>
                </div>
              )}

              {editingDistributor && (
                <>
                  <div className="form-group">
                    <label htmlFor="openingBalance">Opening Balance (Read-Only)</label>
                    <input
                      type="number"
                      id="openingBalance"
                      className="form-control"
                      value={formData.openingBalance}
                      readOnly
                      style={{
                        backgroundColor: '#e3f2fd',
                        cursor: 'not-allowed',
                        fontWeight: '600',
                        color: '#0d47a1'
                      }}
                    />
                    <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                      Historical opening balance (never changes, used for ledger calculations)
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="amountOutstanding">Current Amount Outstanding (Read-Only)</label>
                    <input
                      type="number"
                      id="amountOutstanding"
                      className="form-control"
                      value={formData.amountOutstanding}
                      readOnly
                      style={{
                        backgroundColor: '#fff3e0',
                        cursor: 'not-allowed',
                        fontWeight: '600',
                        color: '#e65100'
                      }}
                    />
                    <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                      Current outstanding amount (automatically calculated from orders and payments)
                    </small>
                  </div>
                </>
              )}

              <div className="modal-buttons">
                <button type="submit" className="btn btn-primary">
                  {editingDistributor ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPaymentModal && (
        <div className="modal-overlay" onClick={handleClosePaymentModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Pay Distributor</h2>
            </div>

            {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {success && <div className="success" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d4edda', color: '#155724', borderRadius: '8px', border: '1px solid #c3e6cb' }}>{success}</div>}

            <form onSubmit={handlePayDistributor}>
              <div className="form-group">
                <CustomDropdown
                  label="Select Distributor"
                  value={paymentData.distributorId}
                  onChange={async (value) => {
                    setPaymentData({ ...paymentData, distributorId: value, billNumber: '', orderId: '' });
                    if (value) {
                      await Promise.all([
                        fetchUnpaidBills(value),
                        fetchOpeningBalanceLimit(value)
                      ]);
                    } else {
                      setUnpaidBills([]);
                      setOpeningBalanceLimit(null);
                    }
                  }}
                  options={[
                    { value: '', label: '-- Select Distributor --' },
                    ...distributors.map(d => ({
                      value: d.id,
                      label: `${d.name} - Outstanding: ₹${parseFloat(d.amount_outstanding || 0).toFixed(2)}`
                    }))
                  ]}
                  placeholder="-- Select Distributor --"
                />
              </div>

              {paymentData.distributorId && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #dee2e6'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Current Outstanding</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '700', color: '#000' }}>
                    ₹{parseFloat(distributors.find(d => d.id === paymentData.distributorId)?.amount_outstanding || 0).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="form-group">
                <CustomDropdown
                  label="Payment Type"
                  value={paymentData.paymentType}
                  onChange={(value) => setPaymentData({ ...paymentData, paymentType: value, billNumber: '', orderId: '' })}
                  options={[
                    { value: 'order_payment', label: 'Order Payment (Pay Bill)' },
                    { value: 'opening_balance_payment', label: 'Opening Balance / Unlogged Bills Payment' },
                    { value: 'advance', label: 'Advance Payment' }
                  ]}
                />
                <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                  {paymentData.paymentType === 'advance'
                    ? 'Advance payment reduces outstanding and allows negative balance'
                    : paymentData.paymentType === 'opening_balance_payment'
                    ? 'Pay against previous outstanding amount or orders without bill numbers'
                    : 'Pay against a specific bill/order'}
                </small>
              </div>

              {paymentData.paymentType === 'order_payment' && paymentData.distributorId && (
                <div className="form-group">
                  <CustomDropdown
                    label="Select Bill Number"
                    value={paymentData.orderId}
                    onChange={(value) => {
                      const selectedBill = unpaidBills.find(b => b.id === value);
                      setPaymentData({
                        ...paymentData,
                        orderId: value,
                        billNumber: selectedBill?.bill_number || ''
                      });
                    }}
                    options={[
                      { value: '', label: '-- Select Bill --' },
                      ...unpaidBills.map(bill => ({
                        value: bill.id,
                        label: `${bill.bill_number || `Order ${new Date(bill.order_date).toLocaleDateString()}`} - Total: ₹${parseFloat(bill.total_amount).toFixed(2)} | Paid: ₹${parseFloat(bill.paid_amount).toFixed(2)} | Remaining: ₹${parseFloat(bill.remaining_amount).toFixed(2)}`
                      }))
                    ]}
                    placeholder="-- Select Bill --"
                  />
                  {unpaidBills.length === 0 && (
                    <small style={{ color: '#856404', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                      No bills found for this distributor
                    </small>
                  )}
                </div>
              )}

              {paymentData.paymentType === 'opening_balance_payment' && paymentData.distributorId && openingBalanceLimit && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #ffc107'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#856404', fontWeight: '600' }}>Opening Balance Payment Limit</p>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span>Opening Balance:</span>
                      <span style={{ fontWeight: '600' }}>₹{openingBalanceLimit.opening_balance.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span>Unlogged Orders:</span>
                      <span style={{ fontWeight: '600' }}>₹{openingBalanceLimit.unlogged_orders_total.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span>Already Paid:</span>
                      <span style={{ fontWeight: '600', color: '#28a745' }}>-₹{openingBalanceLimit.total_paid.toFixed(2)}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '0.5rem',
                      paddingTop: '0.5rem',
                      borderTop: '2px solid #ffc107'
                    }}>
                      <span style={{ fontWeight: '700' }}>Maximum Allowed:</span>
                      <span style={{ fontWeight: '700', fontSize: '1.125rem', color: '#856404' }}>₹{openingBalanceLimit.remaining_limit.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="amount">
                  Amount
                  {paymentData.paymentType === 'opening_balance_payment' && openingBalanceLimit && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#856404', fontWeight: '600' }}>
                      (Max: ₹{openingBalanceLimit.remaining_limit.toFixed(2)})
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  id="amount"
                  className="form-control"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  step="0.01"
                  min="0"
                  max={paymentData.paymentType === 'opening_balance_payment' && openingBalanceLimit ? openingBalanceLimit.remaining_limit : undefined}
                  required
                  placeholder={paymentData.paymentType === 'opening_balance_payment' && openingBalanceLimit
                    ? `Max: ₹${openingBalanceLimit.remaining_limit.toFixed(2)}`
                    : "Enter amount"}
                  style={{ padding: '0.75rem', fontSize: '1rem' }}
                />
                {paymentData.paymentType === 'opening_balance_payment' && openingBalanceLimit && openingBalanceLimit.remaining_limit === 0 && (
                  <small style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block', fontWeight: '600' }}>
                    ⚠️ Opening balance has been fully paid. No payment allowed.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="paymentDate">Payment Date | भुगतान तिथि</label>
                <input
                  type="date"
                  id="paymentDate"
                  className="form-control"
                  value={paymentData.paymentDate}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  style={{ padding: '0.75rem', fontSize: '1rem' }}
                />
                <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                  Date when the payment was made (cannot be in the future)
                </small>
              </div>

              <div className="form-group">
                <CustomDropdown
                  label="Payment From | से भुगतान"
                  value={paymentData.paymentFrom}
                  onChange={(value) => setPaymentData({ ...paymentData, paymentFrom: value })}
                  options={[
                    { value: 'bank_balance', label: 'Bank Balance | बैंक शेष' },
                    { value: 'cash_balance', label: 'Cash Balance | नकद शेष' },
                    { value: 'gala_balance', label: 'Gala Balance | गला शेष' }
                  ]}
                />
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#fff3cd',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#856404'
                }}>
                  💡 Select which balance account to deduct this payment from
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes (Optional)</label>
                <textarea
                  id="notes"
                  className="form-control"
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  placeholder="Add any notes about this payment"
                  rows="2"
                  style={{ padding: '0.75rem', fontSize: '1rem' }}
                />
              </div>

              {paymentData.distributorId && paymentData.amount && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #2196F3'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#1565c0' }}>New Outstanding After Payment</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '700', color: '#0d47a1' }}>
                    ₹{(parseFloat(distributors.find(d => d.id === paymentData.distributorId)?.amount_outstanding || 0) - parseFloat(paymentData.amount || 0)).toFixed(2)}
                  </p>
                  {paymentData.paymentType === 'advance' && (parseFloat(distributors.find(d => d.id === paymentData.distributorId)?.amount_outstanding || 0) - parseFloat(paymentData.amount || 0)) < 0 && (
                    <small style={{ color: '#0d47a1', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                      ✓ Negative balance represents advance/credit with distributor
                    </small>
                  )}
                </div>
              )}

              <div className="modal-buttons">
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    background: '#2196F3',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    borderColor: '#000'
                  }}
                >
                  Pay Distributor
                </button>
                <button type="button" onClick={handleClosePaymentModal} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDistributorHistory && selectedDistributor && (
        <div className="modal-overlay" onClick={handleCloseDistributorHistory}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', width: '95%', maxHeight: '90vh', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Distributor Details - {selectedDistributor.name}</h2>
              <button
                onClick={handleCloseDistributorHistory}
                className="btn btn-secondary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Close
              </button>
            </div>

            {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

            <div style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Current Outstanding</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.25rem', fontWeight: '700', color: '#dc3545' }}>
                    ₹{parseFloat(selectedDistributor.amount_outstanding || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Created</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem', fontWeight: '600' }}>
                    {new Date(selectedDistributor.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              borderBottom: '2px solid #dee2e6',
              marginBottom: '1.5rem',
              overflowX: 'auto',
              flexShrink: 0
            }}>
              <button
                onClick={() => setActiveTab('outstanding')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderBottom: activeTab === 'outstanding' ? '3px solid #2196F3' : '3px solid transparent',
                  background: activeTab === 'outstanding' ? '#e3f2fd' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'outstanding' ? '700' : '600',
                  color: activeTab === 'outstanding' ? '#0d47a1' : '#666',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap'
                }}
              >
                Outstanding Bills ({unpaidBills.filter(b => !b.is_fully_paid).length})
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderBottom: activeTab === 'payments' ? '3px solid #2196F3' : '3px solid transparent',
                  background: activeTab === 'payments' ? '#e3f2fd' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'payments' ? '700' : '600',
                  color: activeTab === 'payments' ? '#0d47a1' : '#666',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap'
                }}
              >
                Payment History ({distributorHistory.length})
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderBottom: activeTab === 'orders' ? '3px solid #2196F3' : '3px solid transparent',
                  background: activeTab === 'orders' ? '#e3f2fd' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'orders' ? '700' : '600',
                  color: activeTab === 'orders' ? '#0d47a1' : '#666',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap'
                }}
              >
                All Orders ({distributorOrders.length})
              </button>
              <button
                onClick={() => setActiveTab('ledger')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderBottom: activeTab === 'ledger' ? '3px solid #2196F3' : '3px solid transparent',
                  background: activeTab === 'ledger' ? '#e3f2fd' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'ledger' ? '700' : '600',
                  color: activeTab === 'ledger' ? '#0d47a1' : '#666',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap'
                }}
              >
                Complete Ledger ({distributorLedger && distributorLedger.transactions ? distributorLedger.transactions.length : 0})
              </button>
            </div>

            {/* Tab Content - Scrollable */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

            {/* Outstanding Bills Tab */}
            {activeTab === 'outstanding' && (
            <div>
            {unpaidBills.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: '1px solid #dee2e6'
              }}>
                <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#666' }}>
                  No bills found for this distributor
                </p>
              </div>
            ) : unpaidBills.filter(b => !b.is_fully_paid).length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#666',
                backgroundColor: '#d4edda',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: '1px solid #c3e6cb'
              }}>
                <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#155724' }}>
                  ✓ All bills are fully paid!
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: '1.5rem', overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Bill Number</th>
                      <th>Order Date</th>
                      <th>Total Amount</th>
                      <th>Paid</th>
                      <th>Remaining</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaidBills
                      .filter(b => !b.is_fully_paid)
                      .map(bill => (
                        <tr key={bill.id}>
                          <td style={{ fontWeight: '600' }}>
                            {bill.bill_number || '-'}
                          </td>
                          <td>{new Date(bill.order_date).toLocaleDateString()}</td>
                          <td>₹{parseFloat(bill.total_amount).toFixed(2)}</td>
                          <td style={{ color: '#2196F3', fontWeight: '600' }}>
                            ₹{parseFloat(bill.paid_amount).toFixed(2)}
                          </td>
                          <td style={{ color: '#dc3545', fontWeight: '700', fontSize: '1.125rem' }}>
                            ₹{parseFloat(bill.remaining_amount).toFixed(2)}
                          </td>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              backgroundColor: parseFloat(bill.paid_amount) > 0 ? '#fff3cd' : '#f8d7da',
                              color: parseFloat(bill.paid_amount) > 0 ? '#856404' : '#721c24'
                            }}>
                              {parseFloat(bill.paid_amount) > 0 ? 'Partial' : 'Unpaid'}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot style={{ borderTop: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <td colSpan="4" style={{ fontWeight: '700', textAlign: 'right', paddingTop: '1rem' }}>
                        Total Outstanding:
                      </td>
                      <td colSpan="2" style={{ fontWeight: '700', fontSize: '1.25rem', color: '#dc3545', paddingTop: '1rem' }}>
                        ₹{unpaidBills
                          .filter(b => !b.is_fully_paid)
                          .reduce((sum, bill) => sum + parseFloat(bill.remaining_amount), 0)
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            </div>
            )}

            {/* Payment History Tab */}
            {activeTab === 'payments' && (
            <div>
            {distributorHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                No payment history found for this distributor
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table">
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Bill Number</th>
                      <th>Amount</th>
                      <th>Paid From</th>
                      <th>Notes</th>
                      <th>Paid By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributorHistory.map(record => {
                      const paidFromDisplay = record.payment_from === 'cash_balance' ? 'Cash' :
                        record.payment_from === 'bank_balance' ? 'Bank' :
                        record.payment_from === 'gala_balance' ? 'Gala' : '-';

                      const paymentTypeDisplay = record.payment_type === 'advance' ? 'Advance' : 'Order Payment';

                      return (
                        <tr key={record.id}>
                          <td>{new Date(record.payment_date).toLocaleDateString()}</td>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '12px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              backgroundColor: record.payment_type === 'advance' ? '#e3f2fd' : '#fff3cd',
                              color: record.payment_type === 'advance' ? '#0d47a1' : '#856404'
                            }}>
                              {paymentTypeDisplay}
                            </span>
                          </td>
                          <td style={{ fontWeight: '600' }}>
                            {record.bill_number || (record.order_date ? new Date(record.order_date).toLocaleDateString() : '-')}
                          </td>
                          <td style={{ color: '#2196F3', fontWeight: '700', fontSize: '1.125rem' }}>
                            ₹{parseFloat(record.amount).toFixed(2)}
                          </td>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              backgroundColor: record.payment_from === 'cash_balance' ? '#fff3cd' :
                                record.payment_from === 'bank_balance' ? '#d1ecf1' :
                                record.payment_from === 'gala_balance' ? '#d4edda' : '#f8f9fa',
                              color: '#000',
                              fontWeight: '600'
                            }}>
                              {paidFromDisplay}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.875rem', color: '#666' }}>
                            {record.notes || '-'}
                          </td>
                          <td>{record.created_by_name || '-'}</td>
                          <td>
                            <button
                              onClick={() => handleDeletePayment(record)}
                              className="btn btn-danger btn-sm"
                              style={{
                                padding: '0.25rem 0.75rem',
                                fontSize: '0.875rem',
                                borderRadius: '4px'
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {distributorHistory.length > 0 && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#e3f2fd',
                borderRadius: '8px',
                border: '1px solid #2196F3'
              }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#1565c0' }}>Total Amount Paid</p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '700', color: '#0d47a1' }}>
                  ₹{distributorHistory.reduce((sum, record) => sum + parseFloat(record.amount || 0), 0).toFixed(2)}
                </p>
              </div>
            )}
            </div>
            )}

            {/* All Orders Tab */}
            {activeTab === 'orders' && (
            <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
              All Orders ({distributorOrders.length})
            </h3>

            {distributorOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '1.5rem' }}>
                No orders found for this distributor
              </div>
            ) : (
              <div style={{ marginBottom: '1.5rem', overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Order Date</th>
                      <th>Bill Number</th>
                      <th>Items</th>
                      <th>Total Amount</th>
                      <th>Payment Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributorOrders.map(order => {
                      const itemsTotal = order.order_data && Array.isArray(order.order_data)
                        ? order.order_data.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
                        : 0;
                      const grandTotal = itemsTotal + parseFloat(order.tax || 0) + parseFloat(order.misc || 0) + parseFloat(order.tcs || 0) + parseFloat(order.tds || 0) - parseFloat(order.scheme || 0) - parseFloat(order.discount || 0);

                      return (
                        <tr key={order.id}>
                          <td>{new Date(order.order_date).toLocaleDateString()}</td>
                          <td style={{ fontWeight: '600' }}>
                            {order.bill_number || '-'}
                          </td>
                          <td>
                            {order.order_data && Array.isArray(order.order_data) ? order.order_data.length : 0} items
                          </td>
                          <td style={{ fontWeight: '700', fontSize: '1.125rem', color: '#2e7d32' }}>
                            ₹{grandTotal.toFixed(2)}
                          </td>
                          <td>
                            {order.payment_outstanding_date
                              ? new Date(order.payment_outstanding_date).toLocaleDateString()
                              : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot style={{ borderTop: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <td colSpan="3" style={{ fontWeight: '700', textAlign: 'right', paddingTop: '1rem' }}>
                        Total Orders Value:
                      </td>
                      <td colSpan="2" style={{ fontWeight: '700', fontSize: '1.25rem', color: '#2e7d32', paddingTop: '1rem' }}>
                        ₹{distributorOrders.reduce((sum, order) => {
                          const itemsTotal = order.order_data && Array.isArray(order.order_data)
                            ? order.order_data.reduce((s, item) => s + parseFloat(item.total || 0), 0)
                            : 0;
                          return sum + itemsTotal + parseFloat(order.tax || 0) + parseFloat(order.misc || 0) + parseFloat(order.tcs || 0) + parseFloat(order.tds || 0) - parseFloat(order.scheme || 0) - parseFloat(order.discount || 0);
                        }, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            </div>
            )}

            {/* Complete Ledger Tab */}
            {activeTab === 'ledger' && (
            <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
              Complete Ledger {distributorLedger && distributorLedger.transactions ? `(${distributorLedger.transactions.length} transactions)` : ''}
            </h3>

            {/* Date Filters */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label htmlFor="startDate" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-control"
                    value={ledgerDateRange.startDate}
                    onChange={(e) => setLedgerDateRange({ ...ledgerDateRange, startDate: e.target.value })}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label htmlFor="endDate" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-control"
                    value={ledgerDateRange.endDate}
                    onChange={(e) => setLedgerDateRange({ ...ledgerDateRange, endDate: e.target.value })}
                  />
                </div>
                <button
                  onClick={handleRefreshLedger}
                  className="btn btn-primary"
                  style={{ marginBottom: 0 }}
                >
                  Apply Filters
                </button>
                {(ledgerDateRange.startDate || ledgerDateRange.endDate) && (
                  <button
                    onClick={() => {
                      setLedgerDateRange({ startDate: '', endDate: '' });
                      handleRefreshLedger();
                    }}
                    className="btn btn-secondary"
                    style={{ marginBottom: 0 }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {!distributorLedger || !distributorLedger.transactions ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '1.5rem' }}>
                Loading ledger...
              </div>
            ) : distributorLedger.transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '1.5rem' }}>
                No transactions found for this date range
              </div>
            ) : (
              <div>
                {/* Opening Balance */}
                <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '2px solid #2196F3' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '700', fontSize: '1.125rem' }}>Opening Balance:</span>
                    <span style={{ fontWeight: '700', fontSize: '1.5rem', color: '#0d47a1' }}>
                      ₹{parseFloat(distributorLedger.openingBalance || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Transactions Table */}
                <div style={{ marginBottom: '1.5rem', overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Bill Number</th>
                        <th>Amount</th>
                        <th>Running Balance</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distributorLedger.transactions.map((transaction, index) => {
                        const isOrder = transaction.transaction_type === 'order';
                        const typeDisplay = isOrder ? 'Order' :
                          transaction.transaction_type === 'advance' ? 'Advance Payment' : 'Order Payment';

                        const debit = parseFloat(transaction.debit_amount || transaction.debit || 0);
                        const credit = parseFloat(transaction.credit_amount || transaction.credit || 0);
                        const amount = debit - credit;
                        const isPositive = amount > 0;

                        return (
                          <tr key={index} style={{ backgroundColor: isOrder ? '#fff3e0' : '#e8f5e9' }}>
                            <td>{new Date(transaction.transaction_date).toLocaleDateString()}</td>
                            <td>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                backgroundColor: isOrder ? '#ff9800' : '#4caf50',
                                color: '#fff'
                              }}>
                                {typeDisplay}
                              </span>
                            </td>
                            <td style={{ fontWeight: '600' }}>
                              {transaction.bill_number || '-'}
                            </td>
                            <td style={{
                              color: isPositive ? '#d32f2f' : '#2e7d32',
                              fontWeight: '700',
                              fontSize: '1.125rem'
                            }}>
                              {amount === 0 ? '-' : `${isPositive ? '+' : ''}₹${Math.abs(amount).toFixed(2)}`}
                            </td>
                            <td style={{ fontWeight: '700', fontSize: '1.125rem', color: '#0d47a1' }}>
                              ₹{parseFloat(transaction.running_balance || 0).toFixed(2)}
                            </td>
                            <td style={{ fontSize: '0.875rem', color: '#666' }}>
                              {transaction.notes || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Closing Balance */}
                <div style={{ padding: '1rem', backgroundColor: '#d4edda', borderRadius: '8px', border: '2px solid #28a745' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '700', fontSize: '1.125rem' }}>Closing Balance:</span>
                    <span style={{ fontWeight: '700', fontSize: '1.5rem', color: '#155724' }}>
                      ₹{parseFloat(distributorLedger.closingBalance || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            </div>
            )}

            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm && deleteTarget !== null}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeletePayment}
        isDeleting={isDeleting}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? This will reverse the transaction and update all related balances."
        details={deleteTarget ? [
          { label: 'Amount', value: `₹${parseFloat(deleteTarget.amount).toFixed(2)}`, highlight: true },
          { label: 'Type', value: deleteTarget.payment_type === 'advance' ? 'Advance Payment' : deleteTarget.payment_type === 'opening_balance_payment' ? 'Opening Balance Payment' : 'Order Payment' },
          { label: 'Payment Date', value: new Date(deleteTarget.payment_date).toLocaleDateString() },
          ...(deleteTarget.bill_number ? [{ label: 'Bill Number', value: deleteTarget.bill_number }] : []),
          ...(deleteTarget.payment_from ? [{ label: 'Paid From', value: deleteTarget.payment_from === 'cash_balance' ? 'Cash Balance' : deleteTarget.payment_from === 'bank_balance' ? 'Bank Balance' : 'Gala Balance' }] : []),
          ...(deleteTarget.notes ? [{ label: 'Notes', value: deleteTarget.notes }] : [])
        ] : []}
        warningMessage={`Deleting this payment will refund ₹${deleteTarget ? parseFloat(deleteTarget.amount).toFixed(2) : '0.00'} to your organization balance and update the distributor's outstanding amount.`}
        confirmButtonText="Delete Payment"
        type="danger"
      />
    </>
  );
};

export default DistributorsTab;
